import {
  AdminInput,
  Box,
  Button,
  Div,
  EditPenIconRed,
  ErrorToast,
  Icon,
  Img,
  Modal,
  P,
  PropsModal,
  SuccessToast,
  deleteLocationVDriveProvider,
  displayPercentage,
  renderProviderIcon,
  renderProviderName,
  upsertLocationVDriveProviderContact,
  reportError,
} from '@vgrubs/components';
import {
  LocationEntity,
  LocationVDriveProviderContractEntity,
} from 'components/src/types/location.types';
import { Provider } from 'components/src/types/shared.types';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';

export const ProviderCommissionsModal = ({
  modalIsOpen,
  closeModal,
  setLoading,
  setData,
  data,
}: PropsModal & {
  modalIsOpen: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setData: Dispatch<SetStateAction<LocationEntity | null>>;
  data: LocationEntity | null;
}) => {
  const [editModal, setEditModal] =
    useState<LocationVDriveProviderContractEntity | null>(null);

  async function handleUpdateProviderContract() {
    if (!editModal) return;
    if (!data) return;
    const beforeCommission = Number(editModal.beforeCommission);
    const afterCommission = Number(editModal.afterCommission);

    // if (
    //   afterCommission > beforeCommission ||
    //   afterCommission === beforeCommission
    // )
    //   return ErrorToast(
    //     'Before commission must be bigger than after commission.'
    //   );

    if (afterCommission > 100 || beforeCommission > 100)
      return ErrorToast('Percentages must be less than 100.');

    const body = {
      before: beforeCommission,
      after: afterCommission,
      provider: editModal.provider,
    };

    try {
      setLoading(true);
      const res = await upsertLocationVDriveProviderContact(data.id, body);

      // Update the data in state
      setData((prev) => {
        if (!prev) return prev;
        const existingContracts = prev.vDriveProviderContracts || [];

        // Check if the provider contract exists
        const providerExists = existingContracts.some(
          (contract) => contract?.provider === editModal?.provider
        );

        const updatedContracts = providerExists
          ? existingContracts.map((contract) =>
              contract.provider === editModal.provider
                ? {
                    ...contract,
                    beforeCommission: body.before,
                    afterCommission: body.after,
                  }
                : contract
            )
          : [
              ...existingContracts,
              {
                provider: editModal.provider,
                beforeCommission: body.before,
                afterCommission: body.after,
              },
            ];

        return { ...prev, vDriveProviderContracts: updatedContracts };
      });

      SuccessToast(res.data.message);
      setEditModal(null);
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function handleRemoveCommission() {
    if (!data || !editModal) return;
    try {
      setLoading(true);
      const res = await deleteLocationVDriveProvider(
        data?.id,
        editModal?.provider
      );

      // Update the data in state
      setData((prev) => {
        if (!prev) return prev;
        const existingContracts = prev.vDriveProviderContracts || [];

        const updatedContracts = existingContracts.map((contract) => {
          if (contract?.provider !== editModal?.provider) {
            return contract;
          }
        });

        return {
          ...prev,
          vDriveProviderContracts: updatedContracts,
        } as LocationEntity;
      });

      SuccessToast(res.data.message);
      setEditModal(null);
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  const handleValueChange = useCallback((value: string, field: string) => {
    setEditModal((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: value,
      } as LocationVDriveProviderContractEntity;
    });
  }, []);

  const isProviderNotPending = (p: Provider | '') =>
    data?.vDriveProviderContracts.find((s) => s?.provider === p);

  return (
    <>
      <Modal
        isOpen={!!modalIsOpen}
        closeModal={closeModal}
        logo={false}
        header="Manage Commission"
      >
        <P $mb="24">
          Adjust restaurant commission fees before and while using vDrive.
        </P>

        {/* Delivery.com is no longer offered; only a stored contract keeps it. */}
        {Object.values(Provider)
          .filter((p) => p !== Provider.delivery || isProviderNotPending(p))
          .map((p, i) => {
            const vDriveProviderContract = data?.vDriveProviderContracts.find(
              (s) => s?.provider === p
            );

            const beforeCommission =
              vDriveProviderContract?.beforeCommission || 0;

            const afterCommission =
              vDriveProviderContract?.afterCommission || 0;

            const contractToEdit = {
              provider: p,
              afterCommission,
              beforeCommission,
            };

            return (
              <Box
                key={i}
                $mb="8"
                $flex
                $between
                $alignCenter
                $pointer
                onClick={() => setEditModal(contractToEdit)}
                $gap="8px"
              >
                <Div $flex $alignCenter $gap="16px" $gapSM="12px">
                  <Img alt="" width="32px" src={renderProviderIcon(p)} />
                  <P $left $bold $m="0">
                    {renderProviderName(p)}
                  </P>
                </Div>
                <Div $flex $gap="16px" $gapSM="8px" $alignCenter>
                  <P $m="0" $right>
                    {vDriveProviderContract ? (
                      <>
                        {displayPercentage(beforeCommission)} -{' '}
                        {displayPercentage(afterCommission)}
                      </>
                    ) : (
                      'Contract Pending'
                    )}
                  </P>

                  <Icon>
                    <EditPenIconRed />
                  </Icon>
                </Div>
              </Box>
            );
          })}

        <Button $selected $w="100%" $mt="40" onClick={() => closeModal?.()}>
          Confirm
        </Button>
      </Modal>

      <Modal
        isOpen={!!editModal}
        closeModal={() => setEditModal(null)}
        logo={false}
        zIndex={110}
        header={`Edit ${renderProviderName(
          editModal?.provider || ''
        )} Commission`}
      >
        <Div $flex $alignCenter $gap="16px">
          <AdminInput
            value={editModal?.beforeCommission || ''}
            onChange={({ target }) =>
              handleValueChange(target.value, 'beforeCommission')
            }
            label="Percentage Before vDrive"
            type="number"
            min={0}
            max={100}
            $textCenter
          />

          <AdminInput
            value={editModal?.afterCommission || ''}
            onChange={({ target }) =>
              handleValueChange(target.value, 'afterCommission')
            }
            label=" Percentage After vDrive"
            type="number"
            min={0}
            max={100}
            $textCenter
          />
        </Div>
        <Button
          $mt="32"
          $selected
          $w="100%"
          onClick={handleUpdateProviderContract}
        >
          Save
        </Button>
        {isProviderNotPending(editModal?.provider || '') && (
          <Button
            $mt="16"
            $selected
            $gray
            $w="100%"
            onClick={handleRemoveCommission}
          >
            Remove
          </Button>
        )}
      </Modal>
    </>
  );
};
